import * as React from 'react';
import { Check, ChevronDown, Lock } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Steps read top to bottom (T-0186): an interview's plan, questionnaire and
 * transcript, or the parts of a form. Mirrors `.steps`, `.step`,
 * `.step-head`, `.step-dot`, `.step-body` in `docs/discovery/prototype/
 * discovery.css`: a numbered dot on a vertical rule (green tick when done,
 * amber when current), a title and a one-line summary, and the body in a
 * bordered box. A step with `onToggle` opens and closes; one without is
 * static (always open). `locked` is a step that waits on an earlier one (a
 * lock in a pale dot, as the Discovery guide shows it).
 */

export type StepState = 'done' | 'current' | 'todo' | 'locked';

export function Steps({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <ol
      className={cn(
        'relative m-0 list-none p-0',
        'before:absolute before:bottom-3.5 before:left-[13px] before:top-3.5 before:w-0.5 before:bg-ink-100',
        className,
      )}
    >
      {children}
    </ol>
  );
}

/**
 * The numbered dot alone (`.step-dot`): a green tick when done, amber when
 * current, a lock in a pale dot when locked, the number otherwise. `Step`
 * draws it on the vertical rule; a sub-tab strip draws the small size before
 * each step's label (`SubTabsNav`), so the two read as one thing.
 */
export function StepDot({
  n,
  state,
  size = 'md',
  className,
}: {
  n: number;
  state: StepState;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const sm = size === 'sm';
  return (
    <span
      aria-hidden="true"
      data-state={state}
      className={cn(
        'relative z-[1] inline-flex flex-none items-center justify-center rounded-full border-2 font-semibold leading-none',
        sm ? 'h-5 w-5 text-[11px]' : 'h-7 w-7 text-meta',
        state === 'done'
          ? 'border-posture-healthy bg-posture-healthy text-paper-0'
          : state === 'current'
            ? 'border-accent-amber-600 bg-accent-amber-600 text-paper-0'
            : state === 'locked'
              ? 'border-ink-100 bg-paper-0 text-ink-300'
              : 'border-ink-200 bg-paper-0 text-ink-500',
        className,
      )}
    >
      {state === 'done' ? (
        <Check className={sm ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      ) : state === 'locked' ? (
        <Lock className={sm ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      ) : (
        n
      )}
    </span>
  );
}

export interface StepProps {
  n: number;
  title: React.ReactNode;
  state: StepState;
  summary?: React.ReactNode;
  /** Present: the head is a button that opens and closes the body. */
  onToggle?: () => void;
  open?: boolean;
  children: React.ReactNode;
}

export function Step({ n, title, state, summary, onToggle, open = true, children }: StepProps) {
  const bodyId = React.useId();
  const head = (
    <>
      <StepDot n={n} state={state} />
      <span
        className={cn(
          'text-[15px] font-semibold',
          state === 'todo' || state === 'locked' ? 'text-ink-500' : 'text-ink-900',
        )}
      >
        {title}
      </span>
      {summary ? (
        <span className="min-w-0 flex-1 truncate text-meta text-ink-500">{summary}</span>
      ) : (
        <span className="flex-1" />
      )}
    </>
  );
  return (
    <li className="relative mb-3">
      {onToggle ? (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
          className="flex w-full items-center gap-3 rounded-card text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          {head}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'h-4 w-4 flex-none text-ink-300 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      ) : (
        <div className="flex w-full items-center gap-3">{head}</div>
      )}
      {open ? (
        <div
          id={bodyId}
          className="ml-10 mt-2.5 rounded-card border border-ink-100 bg-paper-0 px-[18px] py-4"
        >
          {children}
        </div>
      ) : null}
    </li>
  );
}
